import { Drawer, message } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { createDynamicRequest } from "../../../core/data/redux/employee/employeeAction";

interface Props {
  globalReqModalVisible: boolean;
  handleCancel: () => void;
  employeeId: any;
}

const DynamicRequestModal: React.FC<Props> = ({
  globalReqModalVisible,
  handleCancel,
  employeeId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [attachment, setAttachment] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { user } = useSelector((state: any) => state.auth);
  const { loading, success, error } = useSelector(
    (state: any) => state.employee
  );

  const [errors, setErrors] = useState<any>({});
  const [requestData, setRequestData] = useState({
    requestType: "",
    title: "",
    description: "",
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!globalReqModalVisible) {
      resetForm();
    }
  }, [globalReqModalVisible]);

  // Handle success / error
  useEffect(() => {
    if (success && globalReqModalVisible) {
      message.success("Dynamic request submitted successfully");
      resetForm();
      handleCancel();
    }

    if (error) {
      message.error(error);
    }
  }, [success, error]);

  const handleInputChange = (e: any, field: string) => {
    setRequestData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["application/pdf","image/jpeg","image/png","image/jpg","image/webp"];
    if (!allowedTypes.includes(file.type)) {
      message.error("Only PDF and image files are allowed");
      setAttachment(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // file size check (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      message.error("File size must be less than 5MB");
      setAttachment(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setAttachment(file);
  };


  const resetForm = () => {
    setRequestData({
      requestType: "",
      title: "",
      description: "",
    });
    setAttachment(null);
    setErrors({});

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validateForm = () => {
    let valid = true;
    let newErrors: any = {};

    if (!requestData.requestType.trim()) {
      valid = false;
      newErrors.requestType = "Request type is required";
    }

    if (!requestData.title.trim()) {
      valid = false;
      newErrors.title = "Title is required";
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;
    const formData = new FormData();
    formData.append("EmployeeId", employeeId);
    formData.append("RequestType", requestData.requestType);
    formData.append("Title", requestData.title);
    formData.append("Description", requestData.description);
    formData.append("CreatedBy", user?.UserId);
    if (attachment) formData.append("attachment", attachment);

    dispatch(createDynamicRequest(formData));
  };

  return (
    <Drawer
      title="Dynamic Request"
      placement="right"
      width={500}
      onClose={() => {
        resetForm();
        handleCancel();
      }}
      open={globalReqModalVisible}
      style={{ marginTop: 50, paddingBottom: 50 }}
    >
      <form onSubmit={handleSubmit}>
        <div className="modal-body pb-0">
          <div className="row">

            {/* Title */}
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">
                  Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter short title"
                  value={requestData.title}
                  onChange={(e) => handleInputChange(e, "title")}
                />
                {errors.title && (
                  <div className="text-danger">{errors.title}</div>
                )}
              </div>
            </div>

            {/* Request Type */}
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">
                  Request Type <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Work From Home, Laptop Request"
                  value={requestData.requestType}
                  onChange={(e) => handleInputChange(e, "requestType")}
                />
                {errors.requestType && (
                  <div className="text-danger">{errors.requestType}</div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">
                  Description
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Explain your request in detail"
                  value={requestData.description}
                  onChange={(e) => handleInputChange(e, "description")}
                />
                {errors.description && (
                  <div className="text-danger">{errors.description}</div>
                )}
              </div>
            </div>

            {/* Attachment */}
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">Attachment</label>
                <input
                  type="file"
                  className="form-control"
                  ref={fileInputRef}
                  accept=".pdf,image/*"
                  onChange={handleFileChange}
                />
              </div>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-light me-2"
            onClick={() => {
              resetForm();
              handleCancel();
            }}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit Request"}
          </button>
        </div>
      </form>
    </Drawer>
  );
};

export default DynamicRequestModal;
